// transport.ts — HTTP client to chat-service.
//
// All requests go to /chat/* on the configured chatHost. Designed to be
// trivially testable: every public method returns a typed promise and never
// throws on network errors — instead it returns an error envelope so the UI
// can decide how to render.

export type WireVersion = 1;

/** Why the server closed the conversation — drives the widget's ended-sheet
 *  copy. Additive on the wire: an old server omits it and the widget treats
 *  absence as "callback" (the next-business-day promise). */
export type CloseReason = "callback" | "referred" | "ended";

export interface ChoicePrompt {
  prompt: string;
  choices: string[];
  allow_free_text: boolean;
}

export interface StartSessionResponse {
  v: WireVersion;
  session_id: string;
  assistant_text: string;
  /** Deterministic pre-chat welcome ("Hey {first}, my name is {rep} with
   *  {firm}…"). Rendered as the agent's first bubble, then a typing pause,
   *  then assistant_text. Absent on old servers / the legacy path. */
  welcome_text?: string | null;
  choices?: ChoicePrompt;
  voice_available: boolean;
  /** "chat" = a live session opened; "captured" = the prechat submission was
   *  stored as a contact-form lead with NO session (triage gated it, or the
   *  LLM was down). Absent on an old server → treat as "chat" (deploy skew). */
  outcome?: "chat" | "captured";
  /** True when the agent closed the conversation in its FIRST reply
   *  (out-of-scope referral close) — the session is already finalized;
   *  render the reply, then lock. Absent on old servers. */
  ended?: boolean;
  /** Why the first-turn close happened. Absent ⇒ "callback". */
  close_reason?: CloseReason | null;
}

/** Pre-chat form values (the intent gate). Field names match the wire/server
 *  shape so the same object feeds /chat/session and /chat/fallback-message. */
export interface PrechatValues {
  first_name: string;
  last_name: string | null;
  phone: string;
  inquiry: string;
}

// A prechat session start does real work server-side (triage + contextual
// first-reply generation, ~4-9s typical; the server caps itself at ~18s). The
// client cap exists so a hung CONNECTION degrades to the fallback capture
// instead of a dead spinner.
const PRECHAT_START_TIMEOUT_MS = 25_000;
// The fallback capture is a no-LLM write — if it hasn't answered in 12s the
// host is down and waiting longer only holds the visitor on a dead screen
// (the browser's default fetch timeout is minutes).
const FALLBACK_TIMEOUT_MS = 12_000;

export interface TurnResponse {
  v: WireVersion;
  session_id: string;
  assistant_text: string;
  choices?: ChoicePrompt;
  voice_available: boolean;
  voice_active: boolean;
  ended: boolean;
  end_reason?:
    | "natural"
    | "escalated"
    | "idle_timeout"
    | "max_duration"
    | "error";
  /** Why the conversation closed (ended sheets). Absent ⇒ "callback". */
  close_reason?: CloseReason | null;
}

/**
 * Final metadata delivered by the streaming endpoint's "done" event.
 * Mirrors TurnResponse except `assistant_text` was already streamed via
 * onTextDelta callbacks during the request.
 */
export interface StreamTurnFinal {
  session_id: string;
  final_text: string;
  choices: ChoicePrompt | null;
  voice_available: boolean;
  voice_active: boolean;
  ended: boolean;
  end_reason: TurnResponse["end_reason"] | null;
  close_reason: CloseReason | null;
}

export interface StreamCallbacks {
  onTextDelta: (text: string) => void;
  onToolStart?: (name: string) => void;
  onToolEnd?: (name: string, ok: boolean) => void;
}

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string; code: string };

/**
 * True only when an error means the SERVER SESSION is gone (expired/finalized/
 * not found) — the one case where transparently starting a fresh session and
 * retrying is correct. For transient failures (LLM 503, rate-limit 429, network
 * blips) we must NOT start a new session: doing so drops the conversation
 * history and can double-post a lead (the orphaned session gets reaped into its
 * own partial lead). Those are surfaced to the visitor to retry on the SAME
 * session instead.
 */
export function isExpiredSessionError(code: string): boolean {
  return (
    code === "http_404" ||
    code === "http_410" ||
    code === "session_not_found" ||
    code === "session_finalized"
  );
}

export class ChatTransport {
  constructor(private readonly chatHost: string) {}

  private url(path: string): string {
    // Tolerate either "https://chat.acceltra.ai" or "https://chat.acceltra.ai/" config
    return `${this.chatHost.replace(/\/$/, "")}${path}`;
  }

  private async post<T>(path: string, body: unknown, timeoutMs?: number): Promise<Result<T>> {
    // Manual AbortController (not AbortSignal.timeout) for older-Safari reach.
    const controller = timeoutMs ? new AbortController() : null;
    const timer: ReturnType<typeof setTimeout> | null = controller
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;
    try {
      const resp = await fetch(this.url(path), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        // No credentials — chat-service is auth-free for visitor-facing endpoints
        credentials: "omit",
        ...(controller ? { signal: controller.signal } : {}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        return {
          ok: false,
          error: data.error || `HTTP ${resp.status}`,
          code: data.code || `http_${resp.status}`,
        };
      }
      return { ok: true, value: data as T };
    } catch (e) {
      if (controller?.signal.aborted) {
        return { ok: false, error: "request timed out", code: "timeout" };
      }
      return {
        ok: false,
        error: e instanceof Error ? e.message : String(e),
        code: "network",
      };
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  }

  async startSession(
    firmName: string,
    firmToken?: string,
    referrer?: string,
    demoToken?: string,
    initial?: string,
    source?: import("./source").SourceInfo,
    prechat?: PrechatValues,
  ): Promise<Result<StartSessionResponse>> {
    return this.post<StartSessionResponse>(
      "/chat/session",
      {
        v: 1,
        firm_token: firmToken ?? null,
        firm_name: firmName ?? null,
        referrer_url: referrer ?? null,
        initial_user_message: initial ?? null,
        demo_token: demoToken ?? null,
        source: source ?? null,
        prechat: prechat ?? null,
      },
      // Only the prechat flow gets a client timeout: it does real generation
      // work and has a degrade path; bare starts keep today's semantics.
      prechat ? PRECHAT_START_TIMEOUT_MS : undefined,
    );
  }

  /** Capture a lead via /chat/fallback-message — the degrade path when a
   *  prechat session start fails, and the outage form's submit. Carries the
   *  full SourceInfo (the old FallbackForm sent only referrer_url, losing
   *  attribution on exactly the leads captured during an outage). */
  async sendFallbackMessage(
    firmName: string,
    firmToken: string | undefined,
    values: PrechatValues,
    referrer?: string,
    source?: import("./source").SourceInfo,
  ): Promise<Result<{ ok: boolean }>> {
    return this.post<{ ok: boolean }>(
      "/chat/fallback-message",
      {
        v: 1,
        firm_token: firmToken ?? null,
        firm_name: firmName ?? null,
        first_name: values.first_name,
        last_name: values.last_name,
        phone: values.phone,
        message: values.inquiry,
        referrer_url: referrer ?? null,
        source: source ?? null,
      },
      FALLBACK_TIMEOUT_MS,
    );
  }

  async sendTurn(
    sessionId: string,
    userMessage: string,
    voiceUsed = false,
  ): Promise<Result<TurnResponse>> {
    return this.post<TurnResponse>("/chat/turn", {
      v: 1,
      session_id: sessionId,
      user_message: userMessage,
      voice_used: voiceUsed,
    });
  }

  async endSession(sessionId: string, reason: "user_closed" | "navigated_away" | "explicit_end" = "user_closed"): Promise<Result<{ ok: boolean }>> {
    return this.post<{ ok: boolean }>("/chat/end", {
      v: 1,
      session_id: sessionId,
      reason,
    });
  }

  /**
   * Fire-and-forget end-session. Use this from beforeunload / pagehide / unmount
   * cleanup paths where the browser may cancel a regular fetch before it hits
   * the network. navigator.sendBeacon is the spec-blessed way to send a small
   * POST that the browser guarantees to deliver even as the page unloads.
   *
   * Returns true if the beacon was queued by the browser, false if it couldn't
   * be (e.g. payload too large, browser doesn't support it). On false, fall
   * back to a best-effort fetch — but caller should not await it.
   */
  endSessionBeacon(sessionId: string, reason: "user_closed" | "navigated_away" | "explicit_end" = "user_closed"): boolean {
    const payload = JSON.stringify({ v: 1, session_id: sessionId, reason });
    if (typeof navigator === "undefined" || !navigator.sendBeacon) {
      // Fall back to fire-and-forget fetch — may be cancelled but it's all we have
      void this.endSession(sessionId, reason);
      return false;
    }
    // text/plain is a CORS-safelisted content type: the beacon needs NO
    // preflight, so the browser actually delivers it during pagehide. An
    // application/json Blob requires a preflight that never runs as the page
    // unloads — browsers silently dropped the beacon, the session never
    // finalized, and the lead waited for the 10-minute idle reaper. The
    // server parses the body as JSON regardless of content type.
    const body = new Blob([payload], { type: "text/plain;charset=UTF-8" });
    let queued = false;
    try {
      queued = navigator.sendBeacon(this.url("/chat/end"), body);
    } catch {
      queued = false;
    }
    if (!queued) {
      // Beacon rejected (quota / UA quirk) — keepalive fetch is the next-best
      // unload survivor. Same safelisted content type, same no-preflight rule.
      try {
        void fetch(this.url("/chat/end"), {
          method: "POST",
          headers: { "Content-Type": "text/plain;charset=UTF-8" },
          body: payload,
          credentials: "omit",
          keepalive: true,
        });
      } catch {
        /* best-effort */
      }
    }
    return queued;
  }

  /**
   * Streaming turn: fires onTextDelta as the agent produces tokens, then
   * resolves to the final-metadata payload. Falls back automatically if
   * the browser lacks ReadableStream support.
   *
   * The promise resolves AFTER the server has sent its done event. While the
   * stream is in flight the widget renders incrementally via the callbacks.
   *
   * Error semantics match the rest of the transport: never throws on the
   * happy path or network errors — returns a Result envelope.
   */
  async streamTurn(
    sessionId: string,
    userMessage: string,
    voiceUsed: boolean,
    callbacks: StreamCallbacks,
  ): Promise<Result<StreamTurnFinal>> {
    // Older Safari (≤14) doesn't expose response.body. Hard-fall back to
    // the non-streaming endpoint in that case — same final UX, just no
    // incremental rendering.
    const supportsStreaming =
      typeof ReadableStream !== "undefined" &&
      typeof TextDecoder !== "undefined";

    if (!supportsStreaming) {
      const fallback = await this.sendTurn(sessionId, userMessage, voiceUsed);
      if (!fallback.ok) return fallback;
      // Simulate the streaming UX with a single delta so callers don't have
      // to special-case fallback rendering.
      callbacks.onTextDelta(fallback.value.assistant_text);
      return {
        ok: true,
        value: {
          session_id: fallback.value.session_id,
          final_text: fallback.value.assistant_text,
          choices: fallback.value.choices ?? null,
          voice_available: fallback.value.voice_available,
          voice_active: fallback.value.voice_active,
          ended: fallback.value.ended,
          end_reason: fallback.value.end_reason ?? null,
          close_reason: fallback.value.close_reason ?? null,
        },
      };
    }

    let resp: Response;
    try {
      resp = await fetch(this.url("/chat/turn/stream"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify({
          v: 1,
          session_id: sessionId,
          user_message: userMessage,
          voice_used: voiceUsed,
        }),
        credentials: "omit",
      });
    } catch (e) {
      return {
        ok: false,
        error: e instanceof Error ? e.message : String(e),
        code: "network",
      };
    }

    if (!resp.ok) {
      // Drain the response so we can surface a useful error code
      let detail = "";
      try {
        detail = await resp.text();
      } catch { /* ignore */ }
      return {
        ok: false,
        error: detail || `HTTP ${resp.status}`,
        code: `http_${resp.status}`,
      };
    }

    // 2xx but no readable body — e.g. older Safari where fetch `response.body`
    // is null despite ReadableStream/TextDecoder existing. Don't error the
    // turn: fall back to the non-streaming endpoint (same as the hard-fallback
    // above). The server's finalize-time extraction backfill still captures the
    // visitor's data, so this degrades to "no incremental rendering", not a
    // failed turn / lost lead. (Also narrows resp.body to non-null below.)
    if (!resp.body) {
      const fb = await this.sendTurn(sessionId, userMessage, voiceUsed);
      if (!fb.ok) return fb;
      callbacks.onTextDelta(fb.value.assistant_text);
      return {
        ok: true,
        value: {
          session_id: fb.value.session_id,
          final_text: fb.value.assistant_text,
          choices: fb.value.choices ?? null,
          voice_available: fb.value.voice_available,
          voice_active: fb.value.voice_active,
          ended: fb.value.ended,
          end_reason: fb.value.end_reason ?? null,
          close_reason: fb.value.close_reason ?? null,
        },
      };
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let final: StreamTurnFinal | null = null;
    let errorEvent: { error: string; code: string } | null = null;

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by a blank line ("\n\n"). Process every
        // complete event in the buffer; leave any partial event for the next
        // read.
        let sep = buffer.indexOf("\n\n");
        while (sep !== -1) {
          const rawEvent = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          sep = buffer.indexOf("\n\n");

          // We only emit one `data:` line per event from the server side, but
          // be defensive and parse line-by-line.
          for (const line of rawEvent.split("\n")) {
            if (!line.startsWith("data:")) continue;
            const json = line.slice(5).trim();
            if (!json) continue;
            let parsed: Record<string, unknown>;
            try {
              parsed = JSON.parse(json);
            } catch {
              // Malformed event — skip but don't kill the stream
              continue;
            }
            const etype = parsed.type as string;
            if (etype === "text_delta") {
              callbacks.onTextDelta(String(parsed.text ?? ""));
            } else if (etype === "tool_start") {
              callbacks.onToolStart?.(String(parsed.name ?? ""));
            } else if (etype === "tool_end") {
              callbacks.onToolEnd?.(
                String(parsed.name ?? ""),
                Boolean(parsed.ok),
              );
            } else if (etype === "done") {
              final = {
                session_id: String(parsed.session_id ?? sessionId),
                final_text: String(parsed.final_text ?? ""),
                choices: (parsed.choices as ChoicePrompt | null) ?? null,
                voice_available: Boolean(parsed.voice_available),
                voice_active: Boolean(parsed.voice_active),
                ended: Boolean(parsed.ended),
                end_reason:
                  (parsed.end_reason as TurnResponse["end_reason"] | null) ??
                  null,
                close_reason:
                  (parsed.close_reason as CloseReason | null) ?? null,
              };
            } else if (etype === "error") {
              errorEvent = {
                error: String(parsed.message ?? "stream_error"),
                code: "stream_error",
              };
            }
          }
        }
      }
    } catch (e) {
      return {
        ok: false,
        error: e instanceof Error ? e.message : String(e),
        code: "stream_read_failed",
      };
    } finally {
      try { reader.releaseLock(); } catch { /* ignore */ }
    }

    if (errorEvent) {
      return { ok: false, error: errorEvent.error, code: errorEvent.code };
    }
    if (!final) {
      return {
        ok: false,
        error: "stream ended without done event",
        code: "stream_incomplete",
      };
    }
    return { ok: true, value: final };
  }
}
